/**
 * Customer billing — billing details (name, GSTIN, address) and the
 * customer's own bills (invoices, advance receipts, refunds).
 *
 * Endpoints (backend `/api/v1/customer/billing/*`):
 *   GET /states                                  → { states: string[] } (the only valid `state` values)
 *   GET /details                                 → saved billing details (all null before first save)
 *   PUT /details                                 → save billing details (422 on bad GSTIN / pincode / state)
 *   GET /documents?service_request_id=<optional> → issued or cancelled bills
 *   GET /documents/{id}/pdf                      → PDF bytes (auth-gated; fetch as a Blob)
 *
 * Types are hand-written until `pnpm gen:types` picks up the billing endpoints.
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { createKeyFactory, type ApiError } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';

// ---------- types -----------------------------------------------------------

export interface BillingStatesResponse {
  states: string[];
}

export interface BillingDetails {
  customer_id: string | null;
  legal_name:  string | null;
  gstin:       string | null;
  address:     string | null;
  city:        string | null;
  state:       string | null;
  pincode:     string | null;
  updated_at:  string | null;
}

/** PUT body. Send `null` for empty fields; GSTIN goes uppercase. */
export interface BillingDetailsUpdate {
  legal_name: string | null;
  gstin:      string | null;
  address:    string | null;
  city:       string | null;
  state:      string | null;
  pincode:    string | null;
}

export type BillDocumentKind =
  | 'invoice'
  | 'receipt_voucher'
  | 'refund_voucher'
  | 'payout_statement';

export type BillStatus = 'issued' | 'cancelled';

export interface PartyDocument {
  id:                 string;
  number:             string | null;
  document_kind:      BillDocumentKind;
  status:             BillStatus;
  service_request_id: string | null;
  /** Decimal string, e.g. "1180.00". */
  total_amount:       string | null;
  /** YYYY-MM-DD. */
  issue_date:         string | null;
  /** Everything printed on the bill. Template-driven; not read here. */
  snapshot:           unknown;
}

export const BILL_KIND_LABELS: Record<BillDocumentKind, string> = {
  invoice:          'Invoice',
  receipt_voucher:  'Advance receipt',
  refund_voucher:   'Refund',
  payout_statement: 'Statement',
};

/** Same patterns the backend validates against. */
export const GSTIN_PATTERN   = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

// ---------- query keys ------------------------------------------------------

export const billingKeys = createKeyFactory('customer.billing', {
  states:  () => ['states'] as const,
  details: () => ['details'] as const,
  bills:   (serviceRequestId?: string) => ['bills', serviceRequestId ?? 'all'] as const,
});

// ---------- raw API functions -----------------------------------------------

const BASE = '/api/v1/customer/billing';

export async function getBillingStates(signal?: AbortSignal) {
  const { data } = await apiClient.get<BillingStatesResponse>(`${BASE}/states`, { signal });
  return data.states;
}

export async function getBillingDetails(signal?: AbortSignal) {
  const { data } = await apiClient.get<BillingDetails>(`${BASE}/details`, { signal });
  return data;
}

export async function updateBillingDetails(payload: BillingDetailsUpdate) {
  const { data } = await apiClient.put<BillingDetails>(`${BASE}/details`, payload);
  return data;
}

/** The backend pages at most 100 per call, so read every page. */
const PAGE = 100;

export async function listMyBills(serviceRequestId?: string, signal?: AbortSignal) {
  const all: PartyDocument[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { data } = await apiClient.get<PartyDocument[]>(`${BASE}/documents`, {
      params: {
        limit: PAGE,
        offset,
        ...(serviceRequestId ? { service_request_id: serviceRequestId } : {}),
      },
      signal,
    });
    all.push(...data);
    if (data.length < PAGE) return all;
  }
}

/** PDF bytes for one bill. Never link the URL directly: it needs the JWT. */
export async function fetchBillPdf(id: string, signal?: AbortSignal) {
  const { data } = await apiClient.get<Blob>(`${BASE}/documents/${id}/pdf`, {
    responseType: 'blob',
    signal,
  });
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useBillingStates(
  options?: Omit<UseQueryOptions<string[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<string[], ApiError>({
    queryKey: billingKeys.states(),
    queryFn: ({ signal }) => getBillingStates(signal),
    // The list of states and UTs practically never changes.
    staleTime: 24 * 60 * 60 * 1000,
    ...options,
  });
}

export function useBillingDetails(
  options?: Omit<UseQueryOptions<BillingDetails, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BillingDetails, ApiError>({
    queryKey: billingKeys.details(),
    queryFn: ({ signal }) => getBillingDetails(signal),
    ...options,
  });
}

export function useUpdateBillingDetails(
  options?: UseMutationOptions<BillingDetails, ApiError, BillingDetailsUpdate>,
) {
  return useMutation<BillingDetails, ApiError, BillingDetailsUpdate>({
    mutationFn: updateBillingDetails,
    ...options,
    // After `...options` so a caller's callbacks can't skip the cache refresh.
    onSuccess: async (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(billingKeys.details(), data);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useMyBills(
  serviceRequestId?: string,
  options?: Omit<UseQueryOptions<PartyDocument[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<PartyDocument[], ApiError>({
    queryKey: billingKeys.bills(serviceRequestId),
    queryFn: ({ signal }) => listMyBills(serviceRequestId, signal),
    // Keep the full list on screen while it refetches. Not for a single
    // request: switching requests must not flash the previous one's bills.
    placeholderData: serviceRequestId ? undefined : (prev) => prev,
    ...options,
  });
}
